import { useEffect, useState } from "react";
import apiClient, { formatApiErrorDetail } from "@/lib/apiClient";
import { CameraCapture } from "@/components/CameraCapture";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Images, Plus, Loader2, Trash2, Sprout, Bug, Maximize2, ArrowLeftRight } from "lucide-react";
import { PhotoCompare } from "@/components/PhotoCompare";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

function AddPhotoDialog({ field, open, onOpenChange, onAdded }) {
  const [photo, setPhoto] = useState(null);
  const [caption, setCaption] = useState("");
  const [kind, setKind] = useState("soil");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!photo) return toast.error("Take or upload a photo first");
    setBusy(true);
    try {
      const res = await apiClient.post(`/fields/${field.id}/photos`, { image_base64: photo.base64, mime_type: photo.mime, kind, caption });
      onAdded(res.data);
      toast.success("Photo added to the timeline");
      setPhoto(null); setCaption("");
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent data-testid="add-photo-dialog" className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-display">Add a progress photo — {field.name}</DialogTitle>
          <DialogDescription>Take the same spot each season so you can see the change.</DialogDescription>
        </DialogHeader>
        <CameraCapture photo={photo} onPhoto={setPhoto} />
        <div className="grid sm:grid-cols-[140px_1fr] gap-3">
          <div>
            <Label className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Type</Label>
            <div className="flex gap-1 mt-1.5">
              {["soil", "plant"].map((k) => (
                <button key={k} data-testid={`photo-kind-${k}`} onClick={() => setKind(k)} className={`flex-1 h-10 rounded-lg text-sm font-semibold border ${kind === k ? "bg-[#2D6A4F] text-white border-[#2D6A4F]" : "border-stone-300 text-stone-600"}`}>{k === "soil" ? "Soil" : "Plant"}</button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider text-stone-500 font-semibold">Note (optional)</Label>
            <Input data-testid="photo-caption-input" value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="e.g. 3 weeks after planting" className="mt-1.5 bg-white" />
          </div>
        </div>
        <Button data-testid="add-photo-submit-btn" onClick={submit} disabled={busy || !photo} className="h-12 bg-[#2D6A4F] hover:bg-[#143D29] text-white rounded-xl font-semibold">
          {busy ? <><Loader2 className="h-5 w-5 mr-2 animate-spin" /> Saving photo…</> : <><Plus className="h-5 w-5 mr-2" /> Add to timeline</>}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

function Lightbox({ photo, onClose }) {
  const [src, setSrc] = useState(null);
  useEffect(() => {
    if (!photo) return;
    let url;
    apiClient.get(`/photos/${photo.id}/original`, { responseType: "blob" }).then((r) => { url = URL.createObjectURL(r.data); setSrc(url); }).catch(() => setSrc(photo.thumb_data_url));
    return () => { if (url) URL.revokeObjectURL(url); setSrc(null); };
  }, [photo]);
  return (
    <Dialog open={!!photo} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="photo-lightbox" className="max-w-4xl p-2 bg-black border-0">
        <DialogHeader className="sr-only"><DialogTitle>Photo</DialogTitle><DialogDescription>Full-size field photo</DialogDescription></DialogHeader>
        {src ? <img src={src} alt={photo?.caption || "Field photo"} className="w-full max-h-[80vh] object-contain rounded-lg" /> : <div className="h-64 flex items-center justify-center text-white"><Loader2 className="h-6 w-6 animate-spin" /></div>}
        {photo && <div className="text-emerald-50 text-sm px-2 pb-1">{new Date(photo.created_at).toLocaleDateString(undefined, { dateStyle: "long" })}{photo.caption ? ` · ${photo.caption}` : ""}{photo.score != null ? ` · score ${Math.round(photo.score)}` : ""}</div>}
      </DialogContent>
    </Dialog>
  );
}

export function FieldGallery({ field }) {
  const [photos, setPhotos] = useState(null);
  const [adding, setAdding] = useState(false);
  const [view, setView] = useState(null);
  const [compareMode, setCompareMode] = useState(false);
  const [picked, setPicked] = useState([]);
  const [comparing, setComparing] = useState(null);

  const togglePick = (p) => {
    const next = picked.some((x) => x.id === p.id) ? picked.filter((x) => x.id !== p.id) : [...picked, p].slice(-2);
    setPicked(next);
    if (next.length === 2) { setComparing(next); setCompareMode(false); setPicked([]); }
  };

  useEffect(() => { apiClient.get(`/fields/${field.id}/photos`).then((r) => setPhotos(r.data)).catch(() => setPhotos([])); }, [field.id]);

  const remove = async (p) => {
    try { await apiClient.delete(`/photos/${p.id}`); setPhotos((ps) => ps.filter((x) => x.id !== p.id)); } catch { toast.error("Could not delete photo"); }
  };

  return (
    <div className="bg-white rounded-2xl border border-stone-200 p-5 mb-4" data-testid="field-gallery">
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-display font-semibold text-emerald-900 flex items-center gap-2"><Images className="h-4 w-4 text-[#D97706]" /> Photo timeline {photos && <span className="text-xs text-stone-400 font-mono-tech">({photos.length})</span>}</h4>
        <div className="flex gap-2">
          {(photos?.length || 0) >= 2 && (
            <Button data-testid="compare-photos-btn" size="sm" variant={compareMode ? "default" : "outline"} onClick={() => { setCompareMode((c) => !c); setPicked([]); }}
              className={`rounded-full ${compareMode ? "bg-[#D97706] hover:bg-[#78350F] text-white" : "border-stone-300"}`}><ArrowLeftRight className="h-4 w-4 mr-1" /> {compareMode ? `Pick ${2 - picked.length} more` : "Compare"}</Button>
          )}
          <Button data-testid="add-photo-btn" size="sm" onClick={() => setAdding(true)} className="bg-[#2D6A4F] hover:bg-[#143D29] text-white rounded-full"><Plus className="h-4 w-4 mr-1" /> Add photo</Button>
        </div>
      </div>
      {compareMode && <p className="text-xs text-[#78350F] mb-2" data-testid="compare-hint">Tap two photos to compare them side by side.</p>}
      {photos === null ? <div className="text-sm text-stone-500 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
        : photos.length === 0 ? <p className="text-sm text-stone-500" data-testid="gallery-empty">No photos yet. Rechecks and pest diagnoses attached to this field land here automatically — or add one now.</p>
        : (
          <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1" data-testid="gallery-strip">
            {photos.map((p) => (
              <div key={p.id} data-testid={`gallery-photo-${p.id}`} className="relative shrink-0 w-44 group">
                <button onClick={() => (compareMode ? togglePick(p) : setView(p))} className={`block w-44 h-32 rounded-xl overflow-hidden border-2 bg-stone-100 ${picked.some((x) => x.id === p.id) ? "border-[#D97706] ring-2 ring-[#D97706]/40" : "border-stone-200"}`}>
                  {p.thumb_data_url ? <img src={p.thumb_data_url} alt={p.caption || p.kind} className="w-full h-full object-cover group-hover:scale-105 transition-transform" /> : <div className="w-full h-full" />}
                </button>
                <span className={`absolute top-2 left-2 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded text-white ${p.kind === "plant" ? "bg-[#78350F]" : "bg-[#2D6A4F]"}`}>{p.kind === "plant" ? <Bug className="h-3 w-3" /> : <Sprout className="h-3 w-3" />} {p.kind}</span>
                <button onClick={() => setView(p)} className="absolute top-2 right-2 h-6 w-6 rounded bg-black/50 text-white flex items-center justify-center opacity-0 group-hover:opacity-100"><Maximize2 className="h-3 w-3" /></button>
                <div className="mt-1.5 text-xs">
                  <div className="font-semibold text-emerald-950">{new Date(p.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}{p.score != null && <span className="ml-1 font-mono-tech text-stone-500">· {Math.round(p.score)}</span>}</div>
                  <div className="text-stone-500 line-clamp-2 leading-tight">{p.caption || (p.source === "recheck" ? "Soil recheck" : p.source === "pest" ? "Pest diagnosis" : "")}</div>
                </div>
                <button data-testid={`gallery-delete-${p.id}`} onClick={() => remove(p)} className="absolute bottom-10 right-2 h-6 w-6 rounded bg-white/90 text-red-500 flex items-center justify-center opacity-0 group-hover:opacity-100"><Trash2 className="h-3 w-3" /></button>
              </div>
            ))}
          </div>
        )}
      <AddPhotoDialog field={field} open={adding} onOpenChange={setAdding} onAdded={(p) => setPhotos((ps) => [p, ...(ps || [])])} />
      <Lightbox photo={view} onClose={() => setView(null)} />
      {comparing && <PhotoCompare a={comparing[0]} b={comparing[1]} onClose={() => setComparing(null)} />}
    </div>
  );
}
